import React, { useState, useRef, useEffect } from 'react';
import { Button, Form, Spinner, Alert } from 'react-bootstrap';
import { FiMenu } from 'react-icons/fi';
import { get, patch } from '../../../helpers/httpClient';
import { toast } from 'react-toastify';

function Tip({ lines, below, belowStart }) {
  const mod = belowStart ? ' seo-tip--below-start' : below ? ' seo-tip--below' : '';
  return (
    <span className={`seo-tip${mod}`}>
      <span className="seo-tip__icon" aria-label="Info">ⓘ</span>
      <span className="seo-tip__popup" role="tooltip">
        <ul className="seo-tip__list">
          {lines.map((l, i) => <li key={i}>{l}</li>)}
        </ul>
      </span>
    </span>
  );
}

function Menu({ website, onUpdated }) {
  const [saving, setSaving] = useState(false);
  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(website?.menu || []);
  }, [website?.id]);
  const [pages, setPages] = useState([]);
  const [dragPath, setDragPath] = useState(null);
  const [overPath, setOverPath] = useState(null);
  const [overPos, setOverPos] = useState(null);
  const itemRefs = useRef({});

  useEffect(() => {
    if (!website?.id) return;
    get(`websites/${website.id}/pages`)
      .then(resp => setPages(Array.isArray(resp) ? resp : (resp?.pages || [])))
      .catch(() => toast.error('Failed to load pages.'));
  }, [website?.id]);

  if (!website) {
    return <Alert variant="warning">Website not found.</Alert>;
  }

  const addItem = () => {
    setItems(prev => [...prev, { pageId: '', name: '', children: [] }]);
  };

  const getByPath = (list, path) => {
    let node = { children: list };
    for (let i = 0; i < path.length; i++) {
      const idx = path[i];
      node = (node.children || [])[idx];
      if (!node) return undefined;
    }
    return node;
  };

  const setByPath = (list, path, updater) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    let node = { children: next };
    for (let i = 0; i < path.length; i++) {
      node = (node.children || [])[path[i]];
    }
    const updated = updater(node);
    let p = { children: next };
    for (let i = 0; i < path.length - 1; i++) {
      p = p.children[path[i]];
    }
    if (path.length === 0) return updated.children || [];
    p.children[path[path.length - 1]] = updated;
    return next;
  };

  const removeByPath = (list, path) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    let parent = { children: next };
    for (let i = 0; i < path.length - 1; i++) {
      parent = parent.children[path[i]];
    }
    const idx = path[path.length - 1];
    const [removed] = (parent.children || []).splice(idx, 1);
    return [removed, parent.children ? next : (removed ? [] : next)];
  };

  const insertAtPath = (list, path, pos, node) => {
    const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
    const next = clone(list);
    if (pos === 'inside') {
      const target = getByPath(next, path);
      if (target) {
        if (!Array.isArray(target.children)) target.children = [];
        target.children.push(node);
      }
    } else {
      let parent = { children: next };
      for (let i = 0; i < path.length - 1; i++) parent = parent.children[path[i]];
      const idx = path[path.length - 1] + (pos === 'after' ? 1 : 0);
      (parent.children || []).splice(idx, 0, node);
    }
    return next;
  };

  const pathKey = (path) => (path || []).join('.');

  const updateFieldAtPath = (path, field, value) => {
    setItems(prev => {
      const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
      const next = clone(prev);
      const node = getByPath(next, path);
      if (!node) return prev;
      node[field] = value;
      return [...next];
    });
  };

  const onDragStart = (path, e) => {
    setDragPath(path);
    setOverPath(path);
    try { e.dataTransfer.setData('text/plain', path.join('.')); } catch (_) {}
    e.dataTransfer.effectAllowed = 'move';
    const el = itemRefs.current[pathKey(path)];
    if (el && typeof e.dataTransfer.setDragImage === 'function') {
      e.dataTransfer.setDragImage(el, 16, 16);
    }
  };

  const onDragEnter = (path) => {
    if (!dragPath) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent) return;
    if (!overPath || pathKey(path) !== pathKey(overPath)) setOverPath(path);
  };

  const onDragOver = (path, depth, e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (!dragPath) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent) {
      setOverPath(null);
      setOverPos(null);
      return;
    }
    const el = itemRefs.current[pathKey(path)];
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const y = e.clientY - rect.top;
    setOverPath(path);
    setOverPos(y / rect.height < 0.5 ? 'before' : 'after');
  };

  const onDrop = (path, depth, e) => {
    e.preventDefault();
    if (!dragPath || !Array.isArray(dragPath)) return;
    const sameParent = dragPath.length === path.length && dragPath.slice(0, -1).every((v, i) => v === path[i]);
    if (!sameParent || pathKey(path) === pathKey(dragPath)) {
      setDragPath(null); setOverPath(null); setOverPos(null);
      return;
    }
    setItems(prev => {
      const [removed, afterRemove] = removeByPath(prev, dragPath);
      if (!removed) return prev;
      let targetPath = path.slice();
      const fromIdx = dragPath[dragPath.length - 1];
      const toIdx = path[path.length - 1];
      if (fromIdx < toIdx) targetPath[targetPath.length - 1] = toIdx - 1;
      return insertAtPath(afterRemove, targetPath, overPos || 'after', removed);
    });
    setDragPath(null);
    setOverPath(null);
    setOverPos(null);
  };

  const onDragEnd = () => {
    setDragPath(null);
    setOverPath(null);
    setOverPos(null);
  };

  const save = async (e) => {
    e.preventDefault();
    let invalid = '';
    const visit = (node, depth, rowLabel) => {
      if (depth > 2) { invalid = `${rowLabel}: nesting exceeds 2 levels`; return; }
      const hasName = !!(node.name || '').trim();
      const hasPage = !!node.pageId;
      if (!hasName && !hasPage) { invalid = `${rowLabel}: set a label or select a page`; return; }
      const children = node.children || [];
      for (let i = 0; i < children.length && !invalid; i++) {
        visit(children[i], depth + 1, `${rowLabel}.${i + 1}`);
      }
    };
    for (let i = 0; i < items.length && !invalid; i++) visit(items[i], 1, `Row ${i + 1}`);
    if (invalid) { toast.error(invalid); return; }
    try {
      setSaving(true);
      const payload = { menu: items };
      const resp = await patch(`websites/${website.id}/menu`, payload);
      const updated = resp?.website || { ...website, menu: items };
      onUpdated?.(updated);
      toast.success('Menu saved.');
    } catch (e) {
      toast.error('Failed to save menu.');
    } finally {
      setSaving(false);
    }
  };

  const addChild = (path) => setItems(prev => setByPath(prev, path, (node) => ({
    ...node,
    children: [...(node.children || []), { name: '', pageId: '', children: [] }],
  })));

  const renderItem = (it, path, depth) => {
    const pk = pathKey(path);
    const isDragging = dragPath && pathKey(dragPath) === pk;
    const isOver = overPath && pathKey(overPath) === pk;
    const children = it.children || [];

    return (
      <div
        key={pk}
        className={[
          'structure-node',
          isDragging ? 'structure-node--dragging' : '',
          isOver && overPos === 'before' && !isDragging ? 'structure-node--drag-before' : '',
          isOver && overPos === 'after' && !isDragging ? 'structure-node--drag-after' : '',
        ].filter(Boolean).join(' ')}
        ref={(el) => (itemRefs.current[pk] = el)}
      >
        <div
          className="structure-node-header"
          draggable
          onDragStart={(e) => onDragStart(path, e)}
          onDragEnd={onDragEnd}
          onDragEnter={() => onDragEnter(path)}
          onDragOver={(e) => onDragOver(path, depth, e)}
          onDrop={(e) => onDrop(path, depth, e)}
        >
          <div className="structure-node-left">
            <span className="structure-drag-handle" aria-hidden>
              <FiMenu size={12} />
            </span>
            <input
              type="text"
              className="social-url-input"
              value={it.name || ''}
              onChange={(e) => {
                const val = e.target.value;
                setItems(prev => {
                  const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
                  const next = clone(prev);
                  const node = getByPath(next, path);
                  if (!node) return prev;
                  node.name = val;
                  if (val) node.pageId = null;
                  return [...next];
                });
              }}
              placeholder="Link label"
            />
            <select
              className="social-platform-select"
              value={it.pageId || ''}
              onChange={(e) => {
                const val = e.target.value;
                setItems(prev => {
                  const clone = (arr) => (arr || []).map(it => ({ ...it, children: clone(it.children) }));
                  const next = clone(prev);
                  const node = getByPath(next, path);
                  if (!node) return prev;
                  node.pageId = val || null;
                  if (val) node.name = null;
                  return [...next];
                });
              }}
            >
              <option value="">— page —</option>
              {pages.map(p => (
                <option key={p.id} value={p.id}>{p.title || p.slug || p.id}</option>
              ))}
            </select>
          </div>
          <div className="structure-node-actions">
            {depth < 2 && (
              <button
                type="button"
                className="menu-add-sub-btn"
                title="Add child link"
                onClick={() => addChild(path)}
              >↳</button>
            )}
            <button
              type="button"
              className="structure-delete-btn"
              title="Remove"
              onClick={() => setItems(prev => { const [, next] = removeByPath(prev, path); return next; })}
            >×</button>
          </div>
        </div>
        {children.length > 0 && (
          <div className="structure-node-children">
            <div>
              {children.map((ch, idx) => renderItem(ch, [...path, idx], depth + 1))}
            </div>
            {depth < 2 && (
              <button type="button" className="structure-add-child-btn" onClick={() => addChild(path)}>+ Add child</button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="mt-3">
      <div className="structure-header-row">
        <h5 className="mb-0">Menu</h5>
        <div className="structure-controls">
          <button type="button" className="structure-add-child-btn" onClick={addItem}>+ Add item</button>
        </div>
      </div>
      <p className="muted-hint mt-1">Build your site's navigation. Each item links to a page or acts as a label.</p>
      <Form onSubmit={save}>
        {items.length === 0 ? (
          <div className="structure-empty">
            <div className="structure-empty-icon">☰</div>
            <div className="structure-empty-title">No navigation items yet</div>
            <div className="structure-empty-hint">Click "+ Add item" above to build your site's navigation.</div>
          </div>
        ) : (
          items.map((it, idx) => renderItem(it, [idx], 1))
        )}
        <div className="form-actions mt-3">
          <Button type="submit" className="primary-button" disabled={saving}>
            {saving ? (<><Spinner animation="border" size="sm" className="me-2" />Saving...</>) : 'Save menu'}
          </Button>
        </div>
      </Form>
    </div>
  );
}

export default Menu;
